import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { MarkdownContent } from '@/components/markdown/markdown-content';
import type { ComponentType } from '@/components/region';
import { LinkWrapper } from '@/components/link';
import DynamicImage from '@/components/dynamic-image';
import { carouselItemImageWidths } from '@/components/carousel-section';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SvgRight } from '@/components/icons-eataly';
import {
    type EditorialCardVariantId,
    type EditorialCarouselCardBackgroundColorClassName,
} from '@/components/carousel-editorial-card/bg-and-values-map';
import {
    DEFAULT_EDITORIAL_HEADING_TYPE,
    type EditorialHeadingClassId,
    type EditorialHeadingTypeId,
} from '@/components/editorial-heading-tag/editorial-heading-map';
import { EditorialHeadingTag } from '@/components/editorial-heading-tag';
import type { EditorialCardLinkAnalytics } from '@/lib/analytics/editorial-card-analytics';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';

export interface EditorialCardProps {
    headingTypeTitle?: EditorialHeadingTypeId;
    headingClassTitle?: EditorialHeadingClassId;
    subtitle?: string;
    title?: string;
    description?: string;
    linkUrl?: string;
    linkText?: string;
    imageSrc?: string;
    focalPoint?: { x?: string; y?: string };
    backgroundColorClass: EditorialCarouselCardBackgroundColorClassName;
    imageAlt?: string;
    component?: ComponentType;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
    regionId?: string;
    variant?: EditorialCardVariantId;
    paddingEnabledInSquareMode?: boolean;
    shadowEnabled?: boolean;
    className?: string;
    textAlignment?: 'left' | 'center' | 'right';
    linkAnalytics?: EditorialCardLinkAnalytics;
    /** When set, the card opens a drawer instead of using a stretched link. */
    onActivate?: () => void;
    /** Accessible name for the drawer button when the card has no title or image alt. */
    activateAccessibleName?: string;
}

function capitalize(value?: string): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}` : undefined;
}

const variantStyles = {
    standard: {
        imageWrapper: 'w-full aspect-[4/3] lg:max-h-[300px] overflow-hidden',
        image: 'object-cover rounded-t-lg',
        // Width is owned by carousel CarouselItem wrappers; keep the card fluid for grids.
        content: 'gap-3 px-4 pb-4 w-full',
    },
    square: {
        imageWrapper: 'aspect-square w-full',
        image: 'object-cover rounded-lg',
        content: 'gap-3 p-0 w-full',
    },
    icon: {
        imageWrapper: 'size-20 shrink-0 overflow-hidden',
        image: 'object-contain',
        content: 'gap-3 px-0 pb-0 items-center w-full',
    },
} as const satisfies Record<EditorialCardVariantId, { imageWrapper: string; image: string; content: string }>;

export default function EditorialCard({
    imageSrc,
    focalPoint,
    backgroundColorClass,
    title,
    subtitle,
    headingTypeTitle = DEFAULT_EDITORIAL_HEADING_TYPE,
    headingClassTitle,
    className,
    description,
    linkUrl,
    linkText,
    imageAlt,
    variant = 'standard',
    paddingEnabledInSquareMode = false,
    shadowEnabled = true,
    textAlignment = 'left',
    linkAnalytics,
    onActivate,
    activateAccessibleName,
}: EditorialCardProps): ReactElement {
    const { t } = useTranslation('common');
    const styles = variantStyles[variant];
    const formattedSubtitle = capitalize(subtitle);
    const formattedTitle = capitalize(title);
    const trimmedLinkUrl = linkUrl?.trim();
    const trimmedLinkText = linkText?.trim();
    const resolvedImageSrc = imageSrc?.trim();
    const focalX = focalPoint?.x ? `${focalPoint.x}%` : '50%';
    const focalY = focalPoint?.y ? `${focalPoint.y}%` : '50%';
    const isIconVariant = variant === 'icon';
    const isSquareVariant = variant === 'square';
    const linkLabel = trimmedLinkText || formattedTitle || imageAlt?.trim();
    const opensDrawer = Boolean(onActivate);
    const drawerButtonName = formattedTitle || imageAlt?.trim() || activateAccessibleName?.trim();
    const analyticsProps = linkAnalytics
        ? {
              'data-analytics-event': linkAnalytics.event,
              'data-analytics-payload': JSON.stringify(linkAnalytics.payload),
          }
        : undefined;
    const textAlignmentClass = isIconVariant
        ? textAlignment === 'right'
            ? 'text-right'
            : 'text-center'
        : textAlignment === 'center'
          ? 'text-center'
          : textAlignment === 'right'
            ? 'text-right'
            : 'text-left';

    return (
        <div
            className={cn(
                'relative flex h-full w-full flex-col rounded-lg',
                isIconVariant ? 'items-center gap-8 px-4 py-10' : 'gap-4',
                backgroundColorClass,
                shadowEnabled && 'shadow-md',
                className
            )}>
            <div
                className={cn(
                    'relative overflow-hidden',
                    styles.imageWrapper,
                    isSquareVariant && paddingEnabledInSquareMode && 'p-4'
                )}>
                {resolvedImageSrc ? (
                    <DynamicImage
                        src={resolvedImageSrc}
                        alt={resolveImageAlt({ alt: imageAlt, title })}
                        className="h-full w-full"
                        imageProps={{
                            className: cn('h-full w-full', styles.image),
                            style: { objectPosition: `${focalX} ${focalY}` },
                        }}
                        widths={carouselItemImageWidths}
                        loading="lazy"
                    />
                ) : (
                    <div
                        className={cn(
                            'flex h-full w-full items-center justify-center rounded-none bg-secondary/20',
                            styles.image
                        )}>
                        <div className="text-center text-muted-foreground">
                            <div className="mb-2 text-4xl">📷</div>
                            <p>{t('noImageAvailable')}</p>
                        </div>
                    </div>
                )}
            </div>

            <div
                className={cn(
                    'flex flex-1 flex-col justify-between',
                    styles.content,
                    isIconVariant && textAlignmentClass,
                    paddingEnabledInSquareMode && isSquareVariant && 'px-4 pb-4'
                )}>
                {(formattedSubtitle || formattedTitle) && (
                    <div className={cn('flex flex-col gap-1', isIconVariant && 'items-center')}>
                        {formattedSubtitle && (
                            <p className={cn('label-l text-neutral-40', textAlignmentClass)}>{formattedSubtitle}</p>
                        )}
                        {formattedTitle && (
                            <EditorialHeadingTag
                                headingTypeTitle={headingTypeTitle}
                                headingClassTitle={headingClassTitle}
                                className={textAlignmentClass}>
                                {formattedTitle}
                            </EditorialHeadingTag>
                        )}
                    </div>
                )}
                {description && (
                    <MarkdownContent
                        content={description}
                        className={cn(
                            'body-copy-l text-neutral-40 line-clamp-3',
                            textAlignmentClass,
                            isIconVariant && 'text-center'
                        )}
                    />
                )}
                {trimmedLinkUrl && trimmedLinkText && (
                    <Button asChild iconAfter={<SvgRight className="size-4" />} hierarchy="tertiary" size="large">
                        <LinkWrapper
                            className={cn(
                                'w-fit',
                                isIconVariant && 'mx-auto',
                                opensDrawer && 'relative z-3',
                                // Stretched link: one anchor covers the whole card while keeping the visible CTA.
                                !opensDrawer && 'after:absolute after:inset-0 after:z-2 after:content-[""]'
                            )}
                            to={trimmedLinkUrl}
                            {...analyticsProps}>
                            {trimmedLinkText}
                        </LinkWrapper>
                    </Button>
                )}
            </div>
            {opensDrawer && drawerButtonName ? (
                <button
                    type="button"
                    className="absolute inset-0 z-2 cursor-pointer rounded-lg"
                    onClick={onActivate}
                    aria-label={drawerButtonName}
                />
            ) : null}
            {!opensDrawer && trimmedLinkUrl && !trimmedLinkText && linkLabel && (
                <LinkWrapper className="absolute inset-0 z-2 rounded-lg" to={trimmedLinkUrl} {...analyticsProps}>
                    <span className="sr-only">{linkLabel}</span>
                </LinkWrapper>
            )}
        </div>
    );
}
